// src/components/apps/ghouseishosi/GhouseishosiHeader.tsx
import React, { useState, useEffect } from 'react';
import {
    AppBar,
    Toolbar,
    IconButton,
    Avatar,
    Tooltip,
    Typography,
    Box,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ArrowCircleLeftIcon from '@mui/icons-material/ArrowCircleLeft';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import SettingsIcon from '@mui/icons-material/Settings';
import { useAuth } from '@/contexts/AuthProvider';
import { useNavigate } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { useFirestore } from '@/hooks/useFirestore';
import { styled } from '@mui/material/styles';
import AIRoadmapNotificationPopup from '@/components/AIRoadmapNotificationPopup';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '@/store/rootReducer';
import { clearTemporaryPremium } from '@/store/premiumSlice';
import { Avatar as MuiAvatar } from '@mui/material';
import { isAdmin } from '@/utils/adminCheck';

const RainbowAvatarWrapper = styled('div')({
    position: 'relative',
    display: 'inline-block',
    borderRadius: '50%',
    padding: 5,
    background:
        'linear-gradient(45deg, #00FF00, #32CD32, #00FA9A, #3CB371, #2E8B57, #228B22, #006400)'
});

const RainbowAvatar = styled(MuiAvatar)({
    borderRadius: '50%'
});

const GhouseishosiHeader: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const firestore = useFirestore();
    const appId = 'ghouseishosi';

    const temporaryPremium = useSelector(
        (state: RootState) => state.premium.temporaryPremium
    );

    const [premiumFromFS, setPremiumFromFS] = useState(false);
    const [level, setLevel] = useState(1);
    const [userIsAdmin, setUserIsAdmin] = useState(false);
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

    const isPremium = premiumFromFS || temporaryPremium;
    const isMenuOpen = Boolean(menuAnchorEl);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setMenuAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };

    const handleNavigateToRoot = () => {
        navigate('/');
        handleMenuClose();
    };

    useEffect(() => {
        if (!user) return;

        setUserIsAdmin(isAdmin(user.email, user.uid));

        const ref = firestore.getUserDoc(user.uid);
        const unsub = onSnapshot(ref, (snap) => {
            if (!snap.exists()) return;
            const d = snap.data();
            setPremiumFromFS(d.membership === 'premium');
            setLevel(typeof d.level === 'number' ? d.level : 1);
            dispatch(clearTemporaryPremium());
        });
        return () => unsub();
    }, [user, dispatch]);

    const back = () =>
        window.history.length > 1 ? navigate(-1) : navigate(`/${appId}`);
    const toAccount = () => navigate(`/${appId}/account`);

    return (
        <AppBar
            position="fixed"
            sx={{
                backgroundColor: '#2E8B57',
                // ノッチ/ステータスバー(セーフエリア)と被らないよう上部に余白を確保
                paddingTop: 'env(safe-area-inset-top)',
            }}
        >
            <Toolbar sx={{ minHeight: 56 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        borderRight: '1px solid rgba(255,255,255,0.3)',
                        pr: 1,
                        mr: 1
                    }}
                >
                    {/* アプリロゴ */}
                    <Box sx={{
                        pr: { xs: 0.5, sm: 1 },
                        mr: { xs: 0.5, sm: 1 },
                        borderRight: '1px solid rgba(255,255,255,0.3)',
                        display: 'flex',
                        alignItems: 'center'
                    }}>
                        <Tooltip title="ホームに戻る">
                            <IconButton
                                edge="start"
                                color="inherit"
                                onClick={() => navigate('/')}
                                sx={{ p: { xs: 0.5, sm: 1 } }}
                            >
                                <img
                                    src={`${process.env.PUBLIC_URL}/logo.png`}
                                    alt="Home"
                                    style={{ width: 20, height: 20, objectFit: 'contain' }}
                                />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    <IconButton
                        edge="start"
                        color="inherit"
                        onClick={back}
                        sx={{ p: { xs: 0.5, sm: 1 } }}
                    >
                        <ArrowCircleLeftIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                    </IconButton>
                </Box>

                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                    行
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0, sm: 0.25 }, pr: { xs: 0.5, sm: 1 } }}>
                    {userIsAdmin && (
                        <Tooltip title="管理者メニュー">
                            <IconButton
                                color="inherit"
                                onClick={() => navigate(`/${appId}/admin`)}
                                sx={{ p: { xs: 0.5, sm: 1 } }}
                            >
                                <AdminPanelSettingsIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                            </IconButton>
                        </Tooltip>
                    )}

                    <Tooltip title="ブックマーク">
                        <IconButton
                            color="inherit"
                            onClick={() => navigate(`/${appId}/bookmarks`)}
                            sx={{ p: { xs: 0.5, sm: 1 } }}
                        >
                            <BookmarkIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="自分の投稿">
                        <IconButton
                            color="inherit"
                            onClick={() => navigate(`/${appId}/myposts`)}
                            sx={{ p: { xs: 0.5, sm: 1 } }}
                        >
                            <ChatBubbleOutlineIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                        </IconButton>
                    </Tooltip>

                    <AIRoadmapNotificationPopup />

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 0.5,
                            cursor: 'pointer'
                        }}
                        onClick={toAccount}
                    >
                        <IconButton color="inherit" sx={{ p: 0.5 }}>
                            {isPremium ? (
                                <RainbowAvatarWrapper>
                                    <RainbowAvatar
                                        src={user?.photoURL || ''}
                                        alt={user?.displayName || 'User'}
                                        sx={{ width: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 } }}
                                    />
                                </RainbowAvatarWrapper>
                            ) : (
                                <Avatar
                                    src={user?.photoURL || ''}
                                    alt={user?.displayName || 'User'}
                                    sx={{ width: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 } }}
                                />
                            )}
                        </IconButton>
                        <Typography variant="caption" sx={{ fontSize: { xs: '0.6rem', sm: '0.65rem' }, lineHeight: 1 }}>
                            Lv.{level}
                        </Typography>
                    </Box>

                    <Tooltip title="メニュー">
                        <IconButton color="inherit" onClick={handleMenuOpen} sx={{ p: { xs: 0.5, sm: 1 } }}>
                            <MenuIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                        </IconButton>
                    </Tooltip>
                </Box>

                <Menu
                    anchorEl={menuAnchorEl}
                    open={isMenuOpen}
                    onClose={handleMenuClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                >
                    <MenuItem onClick={handleNavigateToRoot}>
                        <ListItemIcon>
                            <HomeIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>資格試験RM TOP</ListItemText>
                    </MenuItem>
                </Menu>
            </Toolbar>
        </AppBar>
    );
};

export default GhouseishosiHeader;
